/**
 * System Settings Page
 * 
 * システム設定管理ページ
 * 主な機能:
 * - 通知設定の管理
 * - APIキーの設定とテスト
 * - マッチングアルゴリズムの設定
 * - Webhookの管理
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Tab,
  Tabs,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material';
import {
  Save,
  Notifications,
  Api,
  Email,
  Sms,
  Delete,
  Add,
  Edit,
  Webhook,
  Analytics,
  BugReport,
  Check,
  Error,
  Dashboard,
} from '@mui/icons-material';
import { SettingsService } from '../services/settings';
import { SystemSettings, Webhook as WebhookType, NotificationSettings, ApiSettings, MatchingSettings, DashboardDisplaySettings } from '../types';
import { validateApiKey } from '../utils/validation';
import TabPanel from '../components/TabPanel';

const Settings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState<NotificationSettings>({
    email: true,
    sms: false,
    newMatches: true,
    projectUpdates: true,
    engineerUpdates: false,
  });
  const [apiSettings, setApiSettings] = useState<ApiSettings>({
    lineToken: '',
    lineSecret: '',
    openaiKey: '',
    claudeKey: '',
  });
  const [matchingSettings, setMatchingSettings] = useState<MatchingSettings>({
    threshold: 80,
    autoSend: false,
    requireAllSkills: false,
    locationWeight: 30,
    skillWeight: 70,
  });
  const [dashboardDisplaySettings, setDashboardDisplaySettings] = useState<DashboardDisplaySettings>({
    showMonthlyRevenue: true,
    showConversionRate: true,
    showMatchingAccuracy: true,
  });
  const [webhooks, setWebhooks] = useState<WebhookType[]>([]);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string } | null>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');


  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [settingsResult, webhooksResult] = await Promise.all([
        SettingsService.getSettings(),
        SettingsService.getWebhooks(),
      ]);

      if (settingsResult.data) {
        setNotifications(settingsResult.data.notifications);
        setApiSettings(settingsResult.data.api);
        setMatchingSettings(settingsResult.data.matching);
        setDashboardDisplaySettings(settingsResult.data.dashboardDisplay);
      }

      if (webhooksResult.data) {
        setWebhooks(webhooksResult.data);
      }
    } catch (error) {
      setErrorMessage('設定の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const settings: SystemSettings = {
        notifications,
        api: apiSettings,
        matching: matchingSettings,
        dashboardDisplay: dashboardDisplaySettings,
      };

      const result = await SettingsService.saveSettings(settings);

      if (result.error) {
        setErrorMessage(result.error);
      } else {
        setSuccessMessage('設定を保存しました');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      setErrorMessage('設定の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleTestApi = async (apiType: 'line' | 'openai' | 'claude') => {
    setTestResults({ ...testResults, [apiType]: null });

    // Validate API key format before testing
    let apiKey = '';
    switch (apiType) {
      case 'line':
        apiKey = apiSettings.lineToken;
        break;
      case 'openai':
        apiKey = apiSettings.openaiKey;
        break;
      case 'claude':
        apiKey = apiSettings.claudeKey;
        break;
    }

    const validation = validateApiKey(apiKey, apiType);
    if (!validation.isValid) {
      setTestResults({
        ...testResults,
        [apiType]: {
          success: false,
          message: validation.errors[0],
        },
      });
      return;
    }

    const result = await SettingsService.testApiConnection(apiType, apiSettings);
    setTestResults({
      ...testResults,
      [apiType]: {
        success: !result.error,
        message: result.error || '接続成功',
      },
    });
  };

  const handleWebhookDelete = async (id: number) => {
    const result = await SettingsService.deleteWebhook(id);
    if (!result.error) {
      setWebhooks(webhooks.filter(w => w.id !== id));
    }
  };

  const handleWebhookTest = async (webhook: WebhookType) => {
    const result = await SettingsService.testWebhook(webhook);
    const message = result.error ? 'テスト失敗しました' : 'テスト成功';
    if (result.error) {
      setErrorMessage(message);
    } else {
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>設定を読み込んでいます...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
        設定
      </Typography>

      {/* タブ */}
      <Card sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, value) => setTabValue(value)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Notifications />} label="通知設定" />
          <Tab icon={<Api />} label="API設定" />
          <Tab icon={<Analytics />} label="マッチング設定" />
          <Tab icon={<Dashboard />} label="ダッシュボード表示" />
          <Tab icon={<Webhook />} label="Webhook設定" />
        </Tabs>
      </Card>

      {/* 通知設定 */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              <Notifications sx={{ mr: 1, verticalAlign: 'middle' }} />
              通知設定
            </Typography>

            <Box sx={{ display: 'grid', gap: 3 }}>
              {/* 通知方法 */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  通知方法
                </Typography>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notifications.email}
                        onChange={(e) => setNotifications({ ...notifications, email: e.target.checked })}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email fontSize="small" />
                        メール通知
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notifications.sms}
                        onChange={(e) => setNotifications({ ...notifications, sms: e.target.checked })}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Sms fontSize="small" />
                        SMS通知
                      </Box>
                    }
                  />
                </Box>
              </Box>

              <Divider />

              {/* 通知内容 */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  通知内容
                </Typography>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notifications.newMatches}
                        onChange={(e) => setNotifications({ ...notifications, newMatches: e.target.checked })}
                      />
                    }
                    label="新しいマッチング候補"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notifications.projectUpdates}
                        onChange={(e) => setNotifications({ ...notifications, projectUpdates: e.target.checked })}
                      />
                    }
                    label="案件の更新・追加"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notifications.engineerUpdates}
                        onChange={(e) => setNotifications({ ...notifications, engineerUpdates: e.target.checked })}
                      />
                    }
                    label="エンジニア情報の更新"
                  />
                </Box>
              </Box>

              <Alert severity="info">
                通知設定は即座に反映されます。重要な通知の見逃しを防ぐため、メール通知の有効化を推奨します。
              </Alert>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* API設定 */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              <Api sx={{ mr: 1, verticalAlign: 'middle' }} />
              API設定
            </Typography>

            <Box sx={{ display: 'grid', gap: 3 }}>
              {/* LINE API */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  LINE API
                </Typography>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                      fullWidth
                      label="チャネルアクセストークン"
                      type="password"
                      value={apiSettings.lineToken}
                      onChange={(e) => setApiSettings({ ...apiSettings, lineToken: e.target.value })}
                      placeholder="長期のチャネルアクセストークンを入力"
                    />
                    <Button
                      variant="outlined"
                      startIcon={<BugReport />}
                      onClick={() => handleTestApi('line')}
                      disabled={!apiSettings.lineToken}
                      size="small"
                    >
                      テスト
                    </Button>
                  </Box>
                  {testResults.line && (
                    <Alert severity={testResults.line.success ? 'success' : 'error'} sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {testResults.line.success ? <Check fontSize="small" /> : <Error fontSize="small" />}
                        {testResults.line.message}
                      </Box>
                    </Alert>
                  )}
                  <TextField
                    fullWidth
                    label="チャネルシークレット"
                    type="password"
                    value={apiSettings.lineSecret}
                    onChange={(e) => setApiSettings({ ...apiSettings, lineSecret: e.target.value })}
                    placeholder="チャネルシークレットを入力"
                  />
                </Box>
              </Box>

              <Divider />

              {/* AI API */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  AI API
                </Typography>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                      fullWidth
                      label="OpenAI APIキー"
                      type="password"
                      value={apiSettings.openaiKey}
                      onChange={(e) => setApiSettings({ ...apiSettings, openaiKey: e.target.value })}
                      placeholder="sk-xxxxxxxxxxxxxxxx"
                    />
                    <Button
                      variant="outlined"
                      startIcon={<BugReport />}
                      onClick={() => handleTestApi('openai')}
                      disabled={!apiSettings.openaiKey}
                      size="small"
                    >
                      テスト
                    </Button>
                  </Box>
                  {testResults.openai && (
                    <Alert severity={testResults.openai.success ? 'success' : 'error'}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {testResults.openai.success ? <Check fontSize="small" /> : <Error fontSize="small" />}
                        {testResults.openai.message}
                      </Box>
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                      fullWidth
                      label="Claude APIキー"
                      type="password"
                      value={apiSettings.claudeKey}
                      onChange={(e) => setApiSettings({ ...apiSettings, claudeKey: e.target.value })}
                      placeholder="Claude APIキーを入力"
                    />
                    <Button
                      variant="outlined"
                      startIcon={<BugReport />}
                      onClick={() => handleTestApi('claude')}
                      disabled={!apiSettings.claudeKey}
                      size="small"
                    >
                      テスト
                    </Button>
                  </Box>
                  {testResults.claude && (
                    <Alert severity={testResults.claude.success ? 'success' : 'error'}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {testResults.claude.success ? <Check fontSize="small" /> : <Error fontSize="small" />}
                        {testResults.claude.message}
                      </Box>
                    </Alert>
                  )}
                </Box>
              </Box>


              <Alert severity="warning">
                APIキーは機密情報です。第三者に共有しないでください。定期的にキーの更新を行うことを推奨します。
              </Alert>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* マッチング設定 */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              <Analytics sx={{ mr: 1, verticalAlign: 'middle' }} />
              マッチング設定
            </Typography>

            <Box sx={{ display: 'grid', gap: 3 }}>
              {/* マッチング閾値 */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  マッチング閾値
                </Typography>
                <TextField
                  fullWidth
                  label="最低マッチ度 (%)"
                  type="number"
                  value={matchingSettings.threshold}
                  onChange={(e) => setMatchingSettings({ ...matchingSettings, threshold: parseInt(e.target.value) })}
                  inputProps={{ min: 0, max: 100 }}
                  helperText="この値以上のマッチ度の場合のみ、マッチング結果として表示されます"
                />
              </Box>

              <Divider />

              {/* 自動送信設定 */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  自動送信設定
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={matchingSettings.autoSend}
                      onChange={(e) => setMatchingSettings({ ...matchingSettings, autoSend: e.target.checked })}
                    />
                  }
                  label="高マッチ度案件の自動送信 (90%以上)"
                />
              </Box>

              <Divider />

              {/* マッチング条件 */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  マッチング条件
                </Typography>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={matchingSettings.requireAllSkills}
                        onChange={(e) => setMatchingSettings({ ...matchingSettings, requireAllSkills: e.target.checked })}
                      />
                    }
                    label="必須スキルをすべて満たす場合のみマッチング"
                  />
                </Box>
              </Box>

              <Divider />

              {/* 重み付け設定 */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  マッチング重み付け
                </Typography>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="スキル重要度 (%)"
                    type="number"
                    value={matchingSettings.skillWeight}
                    onChange={(e) => setMatchingSettings({ ...matchingSettings, skillWeight: parseInt(e.target.value) })}
                    inputProps={{ min: 0, max: 100 }}
                  />
                  <TextField
                    fullWidth
                    label="勤務地重要度 (%)"
                    type="number"
                    value={matchingSettings.locationWeight}
                    onChange={(e) => setMatchingSettings({ ...matchingSettings, locationWeight: parseInt(e.target.value) })}
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Box>
              </Box>

              <Alert severity="info">
                重み付けの合計は100%になるように自動調整されます。スキル重視の設定を推奨します。
              </Alert>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* ダッシュボード表示設定 */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              <Dashboard sx={{ mr: 1, verticalAlign: 'middle' }} />
              ダッシュボード表示設定
            </Typography>

            <Box sx={{ display: 'grid', gap: 3 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  表示項目の設定
                </Typography>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dashboardDisplaySettings.showMonthlyRevenue}
                        onChange={(e) => setDashboardDisplaySettings({ 
                          ...dashboardDisplaySettings, 
                          showMonthlyRevenue: e.target.checked 
                        })}
                      />
                    }
                    label="月間売上を表示"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dashboardDisplaySettings.showConversionRate}
                        onChange={(e) => setDashboardDisplaySettings({ 
                          ...dashboardDisplaySettings, 
                          showConversionRate: e.target.checked 
                        })}
                      />
                    }
                    label="成約率を表示"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dashboardDisplaySettings.showMatchingAccuracy}
                        onChange={(e) => setDashboardDisplaySettings({ 
                          ...dashboardDisplaySettings, 
                          showMatchingAccuracy: e.target.checked 
                        })}
                      />
                    }
                    label="マッチング精度を表示"
                  />
                </Box>
              </Box>

              <Alert severity="info">
                ダッシュボードに表示する統計項目を選択できます。機密性の高い情報は必要に応じて非表示にできます。
              </Alert>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Webhook設定 */}
      <TabPanel value={tabValue} index={4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                <Webhook sx={{ mr: 1, verticalAlign: 'middle' }} />
                Webhook設定
              </Typography>
              <Button variant="contained" startIcon={<Add />} size="small">
                Webhook追加
              </Button>
            </Box>

            <List>
              {webhooks.map((webhook) => (
                <ListItem key={webhook.id} divider>
                  <ListItemText
                    primary={webhook.name}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {webhook.url}
                        </Typography>
                        <Chip
                          label={webhook.status === 'active' ? 'アクティブ' : '無効'}
                          size="small"
                          color={webhook.status === 'active' ? 'success' : 'default'}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      sx={{ mr: 1 }}
                      onClick={() => handleWebhookTest(webhook)}
                      title="Webhookをテスト"
                    >
                      <BugReport />
                    </IconButton>
                    <IconButton edge="end" sx={{ mr: 1 }} title="編集">
                      <Edit />
                    </IconButton>
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => handleWebhookDelete(webhook.id)}
                      title="削除"
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              Webhookを使用して外部サービスと連携できます。新しいマッチング結果やステータス変更時に通知を送信します。
            </Alert>
          </CardContent>
        </Card>
      </TabPanel>

      {/* メッセージ表示 */}
      {successMessage && (
        <Alert severity="success" sx={{ mt: 3 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {errorMessage}
        </Alert>
      )}

      {/* 保存ボタン */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          size="large"
          disabled={saving || loading}
          sx={{ bgcolor: '#27ae60' }}
        >
          {saving ? '保存中...' : '設定を保存'}
        </Button>
      </Box>
    </Box>
  );
};

export default Settings;
